"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Images, ChevronLeft, ChevronRight } from "lucide-react";

interface StayProfileHeroGalleryProps {
  profileId: string;
  businessName: string;
  heroImageUrl?: string | null;
  photoUrls?: string[];
  walkthroughVideoUrl?: string | null;
  /**
   * Owner-written alt text keyed by photo URL.
   *
   * Previously every image rendered as "Name photo 3", which tells a search
   * engine and an AI assistant nothing. Where no description exists we fall
   * back to the old generic string rather than an empty alt, because a missing
   * alt on a meaningful photo is worse than a weak one.
   */
  photoAltText?: Record<string, string>;
  /** Passed to the video player for assistive technology. */
  walkthroughVideoTitle?: string | null;
}

const normalizePhotoUrls = (urls: string[] = []) => {
  const seen = new Set<string>();
  return urls
    .map((url) => (typeof url === "string" ? url.trim() : ""))
    .filter(Boolean)
    .filter((url) => {
      if (seen.has(url)) return false;
      seen.add(url);
      return true;
    });
};

export function StayProfileHeroGallery({
  profileId,
  businessName,
  heroImageUrl,
  photoUrls = [],
  walkthroughVideoUrl,
  photoAltText,
  walkthroughVideoTitle,
}: StayProfileHeroGalleryProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const viewedIndexesRef = useRef<Set<number>>(new Set());
  const touchStartXRef = useRef<number | null>(null);

  /** Owner description for a photo, falling back to the previous generic alt. */
  const altFor = useCallback(
    (url: string, index: number) => {
      const written = photoAltText?.[url]?.trim();
      if (written) return written;
      return url === heroImageUrl
        ? `${businessName} hero image`
        : `${businessName} photo ${index + 1}`;
    },
    [photoAltText, heroImageUrl, businessName],
  );

  /**
   * Visible caption — only what the host actually wrote (e.g. "Kitchen",
   * "Hoopoo room"). The generic alt fallback must never leak into the UI;
   * alt keeps its own fallback for accessibility and SEO.
   */
  const captionFor = useCallback(
    (url: string) => photoAltText?.[url]?.trim() || "",
    [photoAltText],
  );

  const photos = useMemo(() => {
    const cleaned = normalizePhotoUrls(photoUrls);
    if (heroImageUrl && cleaned.includes(heroImageUrl)) {
      return [heroImageUrl, ...cleaned.filter((url) => url !== heroImageUrl)];
    }
    if (heroImageUrl) {
      return [heroImageUrl, ...cleaned];
    }
    return cleaned;
  }, [heroImageUrl, photoUrls]);

  const hasVideo = typeof walkthroughVideoUrl === 'string' && walkthroughVideoUrl.trim().length > 0;
  if (!photos.length && !hasVideo) return null;

  const logEvent = (
    eventType: "gallery_open" | "gallery_photo_view",
    photoIndex?: number,
    sessionId?: string | null,
  ) => {
    fetch(`/api/profile/${encodeURIComponent(profileId)}/gallery-event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventType,
        photoIndex: typeof photoIndex === "number" ? photoIndex : undefined,
        sessionId: sessionId || undefined,
      }),
    }).catch(() => {});
  };

  const openGalleryAt = (index: number) => {
    const sessionId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    setActiveSessionId(sessionId);
    setActiveIndex(index);
    viewedIndexesRef.current = new Set([index]);
    logEvent("gallery_open", undefined, sessionId);
    logEvent("gallery_photo_view", index, sessionId);
    setOpen(true);
  };

  const showNext = () => setActiveIndex((curr) => (curr + 1) % photos.length);
  const showPrev = () => setActiveIndex((curr) => (curr - 1 + photos.length) % photos.length);

  useEffect(() => {
    if (!open) return;
    if (!activeSessionId) return;
    if (viewedIndexesRef.current.has(activeIndex)) return;
    viewedIndexesRef.current.add(activeIndex);
    logEvent("gallery_photo_view", activeIndex, activeSessionId);
  }, [activeIndex, open, activeSessionId]);

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    touchStartXRef.current = event.changedTouches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    const startX = touchStartXRef.current;
    const endX = event.changedTouches[0]?.clientX;
    touchStartXRef.current = null;
    if (startX === null || typeof endX !== "number") return;

    const deltaX = endX - startX;
    if (Math.abs(deltaX) < 40) return;

    if (deltaX < 0) {
      showNext();
    } else {
      showPrev();
    }
  };

  return (
    <>
      {photos.length > 0 ? (
      <Card className="mt-8 overflow-hidden border-slate-200">
        <CardContent className="p-0">
          <button
            type="button"
            className="group relative block aspect-[16/9] w-full"
            onClick={() => openGalleryAt(0)}
            aria-label={`Open ${businessName} photo gallery`}
          >
            <Image
              src={photos[0]}
              alt={altFor(photos[0], 0)}
              fill
              priority
              sizes="(max-width: 768px) 100vw, 1200px"
              className="object-cover transition duration-300 group-hover:scale-[1.02]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white/90">Stay Gallery</p>
                <p className="text-lg font-semibold text-white">Explore {businessName}</p>
              </div>
              <div className="inline-flex items-center rounded-full bg-black/50 px-3 py-1 text-sm font-medium text-white backdrop-blur-sm">
                <Images className="mr-2 h-4 w-4" />
                View {photos.length} photo{photos.length === 1 ? "" : "s"}
              </div>
            </div>
          </button>
          <div className="flex justify-end border-t bg-white px-4 py-3">
            <Button type="button" onClick={() => openGalleryAt(0)} variant="outline">
              <Images className="mr-2 h-4 w-4" />
              View more photos
            </Button>
          </div>
        </CardContent>
      </Card>
      ) : null}

      {hasVideo ? (
        <Card className="mt-4 overflow-hidden border-[#0014891a] bg-gradient-to-br from-white via-slate-50 to-[#00148908] shadow-sm">
          <CardContent className="p-0">
            <div className="grid gap-0 md:grid-cols-[1.3fr_1fr]">
              <div className="relative aspect-video bg-black">
                <video
                  className="h-full w-full object-contain"
                  controls
                  playsInline
                  preload="metadata"
                  poster={photos[0] || undefined}
                  aria-label={
                    walkthroughVideoTitle?.trim() ||
                    `${businessName} property walk-through video`
                  }
                >
                  <source src={walkthroughVideoUrl!.trim()} />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="flex flex-col justify-center gap-3 p-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#001489]">Premium walkthrough</p>
                  <h3 className="mt-1 text-lg font-semibold text-slate-900">See the property before you book</h3>
                  <p className="mt-2 text-sm text-slate-600">
                    A short video helps guests trust what they are seeing without making the page feel heavy.
                  </p>
                </div>
                <div className="rounded-lg border border-[#00148914] bg-white/80 px-3 py-2 text-xs text-slate-600">
                  Lightweight on load, premium on presentation.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setActiveSessionId(null);
            viewedIndexesRef.current = new Set();
          }
        }}
      >
        {open ? (
          <DialogContent className="max-w-5xl p-0 overflow-hidden flex max-h-[92dvh] flex-col">
            <DialogHeader className="border-b px-5 py-4 shrink-0">
              <DialogTitle>{businessName} Photo Gallery</DialogTitle>
              <DialogDescription>
                Swipe or use arrows to browse the full property gallery.
              </DialogDescription>
            </DialogHeader>

            <div
              className="relative aspect-[4/3] sm:aspect-[16/10] max-h-[56dvh] w-full shrink-0 bg-black"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <Image
                src={photos[activeIndex]}
                alt={altFor(photos[activeIndex], activeIndex + 1)}
                fill
                sizes="90vw"
                className="object-contain"
              />

              {photos.length > 1 && (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className="absolute left-3 top-1/2 -translate-y-1/2"
                    onClick={showPrev}
                    aria-label="Previous photo"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    onClick={showNext}
                    aria-label="Next photo"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </>
              )}
            </div>

            {captionFor(photos[activeIndex]) ? (
              <p className="shrink-0 border-t bg-white px-5 py-2 text-center text-sm text-slate-600">
                {captionFor(photos[activeIndex])}
              </p>
            ) : null}

            {photos.length > 1 && (
              <div className="max-h-28 shrink-0 overflow-x-auto border-t bg-white p-3">
                <div className="flex gap-2">
                  {photos.map((url, index) => {
                    const isActive = index === activeIndex;
                    return (
                      <button
                        key={`${url}-${index}`}
                        type="button"
                        onClick={() => setActiveIndex(index)}
                        className={`relative h-16 w-24 shrink-0 overflow-hidden rounded-md border ${isActive ? "border-primary" : "border-transparent"}`}
                        aria-label={`View photo ${index + 1}`}
                      >
                        <Image
                          src={url}
                          alt={altFor(url, index + 1)}
                          fill
                          sizes="96px"
                          className="object-cover"
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </DialogContent>
        ) : null}
      </Dialog>
    </>
  );
}
